import { useCallback, useEffect, useRef } from 'react';
import { Map } from 'shared/map-exports';
import {
  BASE_CIRCLE_RADIUS,
  MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
  MIN_ICON_ZOOM_LEVEL,
  WEATHER_STATIONS_SELECTED_POINT_ICON_LAYER_ID,
} from './WeatherStationsLayer.constants';

const PULSE_CYCLE_SECONDS = 1.5;
const PULSE_AMPLITUDE = 10;

export const usePulseAnimation = (isActive: boolean, map?: Map): void => {
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const setCircleRadius = useCallback(
    (radius: number) => {
      if (!map || !map.isStyleLoaded()) return;
      try {
        map.setPaintProperty(
          WEATHER_STATIONS_SELECTED_POINT_ICON_LAYER_ID,
          'circle-radius',
          radius,
        );
      } catch (error) {
        console.error('usePulseAnimation:setCircleRadius:', error);
      }
    },
    [map],
  );

  const stop = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    startTimeRef.current = null;
    setCircleRadius(0);
  }, [setCircleRadius]);

  const start = useCallback(() => {
    if (!map) return;

    stop(); // Clean up existing animations
    startTimeRef.current = null;

    const animate = (timestamp: number): void => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsedTimeSeconds = (timestamp - startTimeRef.current) / 1000;
      const currentCycle = elapsedTimeSeconds % PULSE_CYCLE_SECONDS;
      // Oscillates between 0 and PULSE_AMPLITUDE * 2 as cycle progresses
      const pulseOffset =
        Math.sin((currentCycle / PULSE_CYCLE_SECONDS) * Math.PI * 2) *
          PULSE_AMPLITUDE +
        PULSE_AMPLITUDE;

      // Calculate base radius based on zoom (matches the point icon circle-radius style expression)
      const zoom = map.getZoom();
      // Normalized progress (0-1)
      const zoomProgress =
        (zoom - MIN_ICON_ZOOM_LEVEL) /
        (MAX_ICON_INTERPOLATION_ZOOM_LEVEL - MIN_ICON_ZOOM_LEVEL);
      // Clamp to [0,1] range
      const clampedProgress = Math.max(0, Math.min(1, zoomProgress));
      // Linear interpolation: BASE_CIRCLE_RADIUS → BASE_CIRCLE_RADIUS * 3
      const baseRadius =
        BASE_CIRCLE_RADIUS + clampedProgress * BASE_CIRCLE_RADIUS * 2;

      setCircleRadius(baseRadius + pulseOffset);

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start the animation loop
    animationRef.current = requestAnimationFrame(animate);
  }, [map, setCircleRadius, stop]);

  useEffect(() => {
    if (isActive) {
      start();
    } else {
      stop();
    }
    return stop; // Cleanup on unmount
  }, [isActive, start, stop]);
};
